import VBox from "sap/m/VBox";
import HBox from "sap/m/HBox";
import Image from "sap/m/Image";
import MessagePage from "sap/m/MessagePage";
import FlexItemData from "sap/m/FlexItemData";
import ResourceModel from "sap/ui/model/resource/ResourceModel";
import Config from "vamos/cockpit/config/Config";
import SplashScreen from "vamos/cockpit/base/util/SplashScreen";

/**
 * Shown when the very first backend call at startup (LoginService#checkLogin) fails because the
 * server is unreachable - i.e. before the vamos.cockpit Component (and with it Component#enterServerUnreachableMode)
 * even exists. Renders directly into #root with no dependency on a Component/router, polls until the
 * backend answers again, then reloads so the normal startup flow (checkLogin -> loadApp) runs again.
 */
export default class ServerUnreachableScreen {

    private static _instance: ServerUnreachableScreen;

    private shown: boolean = false;

    private static readonly POLL_INTERVAL_MS = 5000;

    public static getInstance(): ServerUnreachableScreen {
        if (!ServerUnreachableScreen._instance) {
            ServerUnreachableScreen._instance = new ServerUnreachableScreen();
        }
        return ServerUnreachableScreen._instance;
    }

    public show(): void {
        if (this.shown) {
            return;
        }
        this.shown = true;

        // The app's own stylesheet (manifest sap.ui5.resources.css) is only injected once the
        // Component loads - which never happens on this failure path - so the layout-critical
        // rules are injected here instead of relying on _general.scss's .ServerUnreachablePage /
        // .MaintenanceHeader classes (#root itself has no defined height either, hence "fixed").
        this.injectStyles();

        const i18nModel = new ResourceModel({ bundleName: "vamos.cockpit.assets.i18n.i18n", fallbackLocale: "" });

        const messagePage = new MessagePage({
            showHeader: false,
            icon: "sap-icon://wrench",
            text: "{i18n>ServerUnreachableText}",
            description: "{i18n>ServerUnreachableDescription}",
        });
        messagePage.setLayoutData(new FlexItemData({ growFactor: 1 }));

        const logoHeader = new HBox({
            justifyContent: "Center",
            width: "100%",
            items: [new Image({ src: "assets/mime/logo.svg", height: "60px" }).addStyleClass("sapUiTinyMargin")],
        }).addStyleClass("ServerUnreachableHeader");

        const page = new VBox({ height: "100%", items: [logoHeader, messagePage] }).addStyleClass("ServerUnreachablePage");
        page.setModel(i18nModel, "i18n");
        page.placeAt("root");

        SplashScreen.remove();
        this.pollUntilBackendAvailable();
    }

    private injectStyles(): void {
        const style = document.createElement("style");
        style.textContent = `
            .ServerUnreachablePage { position: fixed; inset: 0; }
            .ServerUnreachableHeader { background-color: #fff; box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08); }
        `;
        document.head.appendChild(style);
    }

    private pollUntilBackendAvailable(): void {
        const config = new Config();
        const intervalId = window.setInterval(() => {
            fetch(config.getRestUrl("system/info", true))
                .then((response) => {
                    if (response.ok) {
                        window.clearInterval(intervalId);
                        window.location.reload();
                    }
                })
                .catch(() => {
                    // still unreachable - keep polling
                });
        }, ServerUnreachableScreen.POLL_INTERVAL_MS);
    }

}
